import React, { useState } from 'react';
import type { TtCourse, CourseStatus } from '../types';

type DetailTab = 'scanner' | 'pending' | 'queue' | 'list';

type Props = {
  course: TtCourse;
  activeTab: DetailTab;
  onChangeTab: (t: DetailTab) => void;
  onBack: () => void;
  onEditCourse?: (c: TtCourse) => void;
  onStatusChange?: (id: number, status: CourseStatus) => void;
  /** Delete course — only triggered AFTER the operator types the
   *  matching course name in the confirm modal owned by this component. */
  onDeleteCourse?: (id: number) => Promise<void> | void;
  /** Live counts shown as badges on tabs. Parent owns the numbers so
   *  they stay fresh as children push/pop records. */
  counts: {
    pending?: number;
    queue?: number;
    list?: number;
  };
  children: React.ReactNode; // active tab's content
  canWrite: boolean;
};

const STATUS_CFG: Record<CourseStatus, { label: string; icon: string; cls: string }> = {
  open: { label: 'Đang mở', icon: 'lock_open', cls: 'open' },
  pending_entry: { label: 'Chờ nhập', icon: 'hourglass_top', cls: 'pending' },
  closed: { label: 'Đã đóng', icon: 'lock', cls: 'closed' },
};

/**
 * Level-2 wrapper: breadcrumb + course summary card + 4-tab bar.
 * Parent renders the tab content as children, so the heavy scanner /
 * pending / queue / list components plug in unchanged.
 */
const CourseDetailPage: React.FC<Props> = ({
  course, activeTab, onChangeTab, onBack, onEditCourse, onStatusChange,
  onDeleteCourse, counts, children, canWrite,
}) => {
  // Delete-confirm modal — operator must retype the course name to
  // arm the destructive action. Catches mistakenly-clicked deletes
  // and forces them to look at the name they're about to wipe.
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const deleteMatches = deleteTyped.trim() === course.name.trim();
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteTyped('');
    setDeleting(false);
  };
  const confirmDelete = async () => {
    if (!deleteMatches || !onDeleteCourse || deleting) return;
    setDeleting(true);
    try {
      await onDeleteCourse(course.id);
      // Parent unmounts this component on success — no need to reset
      // state here. If onDeleteCourse throws, fall through to finally.
    } finally {
      // Only reset if still mounted (failed delete leaves modal open).
      setDeleting(false);
    }
  };
  const cfg = STATUS_CFG[course.status];
  const unreviewed = Math.max(0, (course.recordCount || 0) - (course.reviewedCount || 0));
  const quota = course.maxReviewed;
  const quotaPct = quota && quota > 0
    ? Math.min(100, Math.round(((course.reviewedCount || 0) / quota) * 100))
    : null;
  const quotaFull = quota != null && (course.reviewedCount || 0) >= quota;

  // Esc key to go back to the table (keyboard-friendly).
  // Skip when any popup/modal is open — those components portal their
  // own DOM to document.body and handle Esc themselves. Walking by
  // class name is the pragmatic way to detect them without wiring a
  // global "popup-open" context just for this shortcut.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Defensive: if a modal handled this Esc and called preventDefault,
      // skip — otherwise we'd back out of the course detail right after
      // its modal closed (bubble-phase race with capture-phase modals).
      if (e.defaultPrevented) return;
      const t = e.target as HTMLElement;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      // Any modal / overlay currently mounted → let it handle Esc.
      const POPUP_SELECTORS = [
        '.tt-detail-modal__overlay',
        '.tt-record-detail-modal__overlay',
        '.tt-modal-overlay',
        '.tt-scanner__avatar-zoom',
      ].join(',');
      if (document.querySelector(POPUP_SELECTORS)) return;
      onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);

  return (
    <div className="tt-course-detail">
      {/* Breadcrumb */}
      <div className="tt-course-detail__breadcrumb">
        <button type="button" className="tt-course-detail__back" onClick={onBack} title="Quay lại (Esc)">
          <i className="material-icons">arrow_back</i>
          <span>Quản lý khóa học</span>
        </button>
        <span className="tt-course-detail__breadcrumb-sep">/</span>
        <span className="tt-course-detail__breadcrumb-current">{course.name}</span>
      </div>

      {/* Summary card */}
      <div className="tt-course-detail__summary">
        <div className="tt-course-detail__summary-head">
          <div className="tt-course-detail__summary-title">
            <span className={`tt-course-chip__status tt-course-chip__status--${cfg.cls}`}>
              <i className="material-icons">{cfg.icon}</i>
              {cfg.label}
            </span>
            <span className="tt-course-chip__hang-badge">Hạng {course.hang}</span>
            {course.isNangHang && (
              <span className="tt-course-detail__nang-hang">
                <i className="material-icons">upgrade</i> Nâng hạng
              </span>
            )}
            <h2 className="tt-course-detail__summary-name">{course.name}</h2>
          </div>
          {canWrite && (
            <div className="tt-course-detail__summary-actions">
              {onEditCourse && (
                <button type="button" className="tt-btn tt-btn--outline tt-btn--sm" onClick={() => onEditCourse(course)}>
                  <i className="material-icons">edit</i> Sửa khóa
                </button>
              )}
              {onStatusChange && course.status === 'open' && (
                <button type="button" className="tt-btn tt-btn--outline tt-btn--sm" onClick={() => onStatusChange(course.id, 'closed')}>
                  <i className="material-icons">lock</i> Đóng khóa
                </button>
              )}
              {onStatusChange && course.status === 'closed' && (
                <button type="button" className="tt-btn tt-btn--outline tt-btn--sm" onClick={() => onStatusChange(course.id, 'open')}>
                  <i className="material-icons">lock_open</i> Mở lại
                </button>
              )}
              {onDeleteCourse && course.status === 'closed' && (
                <button
                  type="button"
                  className="tt-btn tt-btn--outline tt-btn--sm tt-btn--danger-outline"
                  onClick={() => setDeleteModalOpen(true)}
                  title="Xóa khóa (cần xác nhận tên)"
                >
                  <i className="material-icons">delete</i> Xóa khóa
                </button>
              )}
            </div>
          )}
        </div>

        <div className="tt-course-detail__stats">
          <span className="tt-course-detail__stat">
            <i className="material-icons">people</i>
            <strong>{course.recordCount ?? 0}</strong>
            <em>hồ sơ</em>
          </span>
          <span className="tt-course-detail__stat tt-course-detail__stat--success">
            <i className="material-icons">check_circle</i>
            <strong>{course.reviewedCount ?? 0}</strong>
            <em>đã duyệt</em>
          </span>
          <span className="tt-course-detail__stat tt-course-detail__stat--primary">
            <i className="material-icons">assignment_turned_in</i>
            <strong>{course.enteredCsdtCount ?? 0}</strong>
            <em>CSĐT</em>
          </span>
          {unreviewed > 0 && (
            <span className="tt-course-detail__stat tt-course-detail__stat--warning">
              <i className="material-icons">pending</i>
              <strong>{unreviewed}</strong>
              <em>chờ duyệt</em>
            </span>
          )}
          <span className="tt-course-detail__stat tt-course-detail__stat--muted">
            <i className="material-icons">event</i>
            <strong>{new Date(course.createdAt).toLocaleDateString('vi-VN')}</strong>
            <em>ngày tạo</em>
          </span>
          <span className="tt-course-detail__stat tt-course-detail__stat--muted">
            <i className="material-icons">person</i>
            <strong>{course.creator?.username || '—'}</strong>
            <em>người tạo</em>
          </span>
        </div>

        {quota != null && (
          <div className={`tt-course-detail__quota${quotaFull ? ' is-full' : ''}`}>
            <div className="tt-course-detail__quota-label">
              <span>Giới hạn duyệt: {course.reviewedCount ?? 0} / {quota}</span>
              <span>{quotaPct}%</span>
            </div>
            <div className="tt-course-detail__quota-bar">
              <div className="tt-course-detail__quota-fill" style={{ width: `${quotaPct}%` }} />
            </div>
            {quotaFull && (
              <div className="tt-course-detail__quota-warn">
                ⚠ Khóa đã đạt giới hạn duyệt. Không thể duyệt thêm hồ sơ mới.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tt-course-detail__tabs">
        <button
          type="button"
          className={`tt-course-detail__tab ${activeTab === 'scanner' ? 'is-active' : ''}`}
          onClick={() => onChangeTab('scanner')}
        >
          <i className="material-icons">edit_note</i>
          Nhập trực tiếp
        </button>
        <button
          type="button"
          className={`tt-course-detail__tab ${activeTab === 'pending' ? 'is-active' : ''}`}
          onClick={() => onChangeTab('pending')}
        >
          <i className="material-icons">pending_actions</i>
          Chờ lưu hồ sơ
          {counts.pending != null && counts.pending > 0 && (
            <span className="tt-course-detail__tab-badge">{counts.pending}</span>
          )}
        </button>
        <button
          type="button"
          className={`tt-course-detail__tab ${activeTab === 'queue' ? 'is-active' : ''}`}
          onClick={() => onChangeTab('queue')}
        >
          <i className="material-icons">inbox</i>
          Hàng đợi giáo viên
          {counts.queue != null && counts.queue > 0 && (
            <span className="tt-course-detail__tab-badge">{counts.queue}</span>
          )}
        </button>
        <button
          type="button"
          className={`tt-course-detail__tab ${activeTab === 'list' ? 'is-active' : ''}`}
          onClick={() => onChangeTab('list')}
        >
          <i className="material-icons">list_alt</i>
          Danh sách hồ sơ
          {counts.list != null && counts.list > 0 && (
            <span className="tt-course-detail__tab-badge tt-course-detail__tab-badge--info">{counts.list}</span>
          )}
        </button>
      </div>

      {/* Active tab content */}
      <div className="tt-course-detail__content">
        {children}
      </div>

      {/* Delete-confirm modal — operator must retype the course name
          before the destructive action arms. Stored hồ sơ count is
          shown so they realise what's about to disappear. */}
      {deleteModalOpen && onDeleteCourse && (
        <div
          className="tt-course-detail__delete-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) closeDeleteModal(); }}
        >
          <div className="tt-course-detail__delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tt-course-detail__delete-head">
              <i className="material-icons">warning</i>
              <h3>Xóa khóa học</h3>
              <button
                type="button"
                className="tt-course-detail__delete-close"
                onClick={closeDeleteModal}
                disabled={deleting}
                aria-label="Đóng"
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="tt-course-detail__delete-body">
              <p className="tt-course-detail__delete-warn">
                Hành động này <strong>không thể hoàn tác</strong>. Toàn bộ <strong>{course.recordCount ?? 0}</strong> hồ sơ và dữ liệu liên quan trong khóa sẽ bị xóa vĩnh viễn.
              </p>
              <p className="tt-course-detail__delete-prompt">
                Để xác nhận, gõ lại tên khóa: <strong>{course.name}</strong>
              </p>
              <input
                type="text"
                className="tt-course-detail__delete-input"
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder="Nhập tên khóa để xác nhận"
                autoFocus
                disabled={deleting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && deleteMatches) void confirmDelete();
                  if (e.key === 'Escape' && !deleting) closeDeleteModal();
                }}
              />
            </div>
            <div className="tt-course-detail__delete-foot">
              <button
                type="button"
                className="tt-btn tt-btn--outline"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Hủy
              </button>
              <button
                type="button"
                className="tt-btn tt-btn--danger"
                onClick={confirmDelete}
                disabled={!deleteMatches || deleting}
                title={deleteMatches ? 'Xóa khóa học' : 'Tên khóa chưa khớp'}
              >
                <i className="material-icons">delete_forever</i>
                {deleting ? 'Đang xóa…' : 'Xóa khóa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailPage;
